import React, { useEffect, useState } from "react";
import Layout from "../Layout";
import "../Dashboard/Dashboard.css";
import api from "../../utils/api";
import AddEditView from "./AddEditView";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editMode, setEditMode] = useState("add");
  const [editingUser, setEditingUser] = useState(null);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api
      .getUsers()
      .then((data) => {
        // console.log("api.getUsers response:", data);
        if (!mounted) return;
        if (Array.isArray(data)) setUsers(data);
        else if (data && data.users) setUsers(data.users);
        else setUsers([]);
      })
      .catch((err) => {
        console.error("api.getUsers error:", err);
        console.error("Failed to load users:", err);
        if (mounted) {
          setError(err.message || "Failed to load users");
          setUsers([]);
          toast.error(err.message || "Failed to load users");
        }
      })
      .finally(() => mounted && setLoading(false));

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <Layout>
      <div className="dashboard">
        <div className="dashboard-content">
          <main className="dashboard-main">
            <div className="overview-content">
              <div
                className="content-header"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div>
                  <h2 style={{ margin: 0 }}>User Management</h2>
                  <p style={{ margin: 0 }}>
                    Manage users and roles for the portal
                  </p>
                </div>
                <div>
                  <button
                    className="btn-add"
                    onClick={() => {
                      setEditMode("add");
                      setEditingUser(null);
                      setShowAddForm(true);
                    }}
                    title="Add user"
                  >
                    Add
                  </button>
                </div>
              </div>

              <div className="content-section">
                <div className="surveys-table">
                  <div className="table-header">
                    <div>Name</div>
                    <div>Email</div>
                    <div>Role</div>
                    <div>Phone</div>
                    <div>Actions</div>
                  </div>

                  {loading && <div className="table-row">Loading users...</div>}
                  {/* {error && <div className="table-row">Error: {error}</div>}
                  {message && <div className="table-row">{message}</div>} */}
                  {!loading && !error && users.length === 0 && (
                    <div className="table-row">No users found.</div>
                  )}
                  {!loading &&
                    users.map((user) => (
                      <div key={user.email || user.name} className="table-row">
                        <div className="name">{user.name || "-"}</div>
                        <div className="email">{user.email || "-"}</div>
                        <div className="role">{user.role || "-"}</div>
                        <div className="phone">{user.phone || "-"}</div>
                        <div className="action-buttons">
                          <button
                            className="btn-action"
                            onClick={() => {
                              setEditMode("view");
                              setEditingUser(user);
                              setShowAddForm(true);
                            }}
                          >
                            View
                          </button>
                          <button
                            className="btn-action"
                            onClick={() => {
                              setEditMode("edit");
                              setEditingUser(user);
                              setShowAddForm(true);
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="btn-action btn-delete"
                            onClick={async () => {
                              const ok = window.confirm(
                                `Delete user ${user.email || user.name}?`
                              );
                              if (!ok) return;
                              try {
                                setMessage("Deleting user...");
                                const identifier = user.id || user.email;
                                await api.deleteUser(identifier);
                                setUsers((prev) =>
                                  prev.filter(
                                    (u) =>
                                      (u.id || u.email) !==
                                      (user.id || user.email)
                                  )
                                );
                                setMessage("User deleted");
                                toast.success("User deleted successfully");
                              } catch (err) {
                                console.error("Delete failed:", err);
                                setMessage(
                                  err.message || "Failed to delete user"
                                );
                                toast.error(
                                  err.message || "Failed to delete user"
                                );
                              }
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
              <AddEditView
                visible={showAddForm}
                onClose={() => {
                  setShowAddForm(false);
                  setEditingUser(null);
                  setMessage(null);
                }}
                onSave={async (user) => {
                  // if adding, call API to create user
                  if (editMode === "add") {
                    try {
                      setMessage("Creating user...");
                      const res = await api.createUser(user);
                      // backend may return created user under data or user
                      const created = Array.isArray(res)
                        ? res[0]
                        : res.user || res;
                      setUsers((prev) => [created, ...prev]);
                      setMessage("User created successfully");
                      setShowAddForm(false);
                      setEditingUser(null);
                      toast.success("User created successfully");
                    } catch (err) {
                      console.error("Create user failed:", err);
                      setMessage(err.message || "Failed to create user");
                      toast.error(err.message || "Failed to create user");
                      // keep modal open for user to correct
                      throw err;
                    }
                  } else if (editMode === "edit") {
                    try {
                      setMessage("Updating user...");
                      // determine identifier for user. Prefer id, fallback to email
                      const identifier =
                        editingUser && (editingUser.id || editingUser.email)
                          ? editingUser.id || editingUser.email
                          : user.email;

                      // build payload — include password fields only when provided (non-empty)
                      const payload = {
                        name: user.name,
                        email: user.email,
                        role: user.role,
                        is_portal: !!user.is_portal,
                        phone: user.phone || null,
                      };
                      if (user.password && String(user.password).trim() !== "")
                        payload.password = user.password;
                      if (
                        user.password_confirmation &&
                        String(user.password_confirmation).trim() !== ""
                      )
                        payload.password_confirmation =
                          user.password_confirmation;

                      const res = await api.updateUser(identifier, payload);
                      const updated = res.user || res;
                      setUsers((prev) =>
                        prev.map((u) =>
                          u.email ===
                          (editingUser && editingUser.email
                            ? editingUser.email
                            : updated.email)
                            ? { ...u, ...updated }
                            : u
                        )
                      );
                      setMessage("User updated");
                      setShowAddForm(false);
                      setEditingUser(null);
                      toast.success("User updated successfully");
                    } catch (err) {
                      console.error("Update user failed:", err);
                      setMessage(err.message || "Failed to update user");
                      toast.error(err.message || "Failed to update user");
                      throw err;
                    }
                  }
                }}
                initialData={editingUser}
                mode={editMode}
              />
            </div>
          </main>
        </div>
      </div>
    </Layout>
  );
};

export default UserManagement;
